/**
 * Protected Route Component
 *
 * Reads auth state from AuthContext (Supabase session) and user profile
 * from useUserStore (DB profile, synced on sign-in).
 *
 * Redirects: not authenticated → /, not approved → /waitlist,
 * approved but not onboarded → /, onboarded but in no household → /
 * (create-or-join fork), already onboarded at / → /today.
 *
 * The `approved` branch is dormant in an open beta (YAR-412): signups are
 * approved as they are provisioned, so nobody reaches /waitlist. It stays
 * wired because it is the landing spot for the two ways access can still
 * be withheld — `app_config.signups_open = 'false'` pausing registrations
 * globally, and an admin un-approving a single user from /admin.
 *
 * Note: `/` is the auth entry point — it renders <Onboarding /> which
 * handles StepWelcome (for signed-out) and OnboardingFlow (for signed-in
 * but incomplete), and redirects completed users to /today on its own.
 */

import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import { AppSkeleton } from '@/components/states/PageSkeletons';
import { savePostAuthRedirect } from '@/lib/postAuthRedirect';
import { useHouseholdlessAccount } from '@/hooks/useHouseholdlessAccount';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const approved = useUserStore((state) => state.approved);
  const onboardingCompletedAt = useUserStore(
    (state) => state.onboardingCompletedAt,
  );
  // An onboarded account that belongs to no family (left its only one, was
  // removed, or the household was deleted) has nothing to show here — the
  // shell would paint empty with no way out. See useHouseholdlessAccount.
  const householdless = useHouseholdlessAccount(
    !loading && !!user && approved && onboardingCompletedAt !== null,
  );
  // Set once this guard has shown the app. A household check that starts
  // MID-session (e.g. a re-resolution after a network blip) must not unmount
  // the user's open screen into a skeleton: the shell stays until the answer
  // is a confirmed 'none'. The skeleton is only for the initial load.
  const shownChildren = useRef(false);

  // Show skeleton while Supabase session initializes
  if (loading) {
    return <AppSkeleton />;
  }

  // Not authenticated — StepWelcome handles sign-in. Preserve the attempted
  // destination (YAR-267 AC11) so an SMS-digest deep link like `/t/:id`
  // isn't lost: `Onboarding` reads it back once the user is signed in AND
  // already onboarded (an onboarding-incomplete user always lands on
  // `/today` after finishing setup instead — see Onboarding.tsx). Stashed
  // in sessionStorage, which survives the real OAuth-provider round trip
  // that wipes router state (nothing reads router `state` for this, since
  // that round trip is exactly the case that needs preserving).
  if (!user) {
    const attempted = `${location.pathname}${location.search}`;
    savePostAuthRedirect(attempted);
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  // Access withheld — see the header note. `approved` is also false while
  // the store is still empty, but AuthContext awaits the profile before it
  // clears `loading`, so that state is already behind the skeleton above.
  if (!approved) {
    return (
      <Navigate
        to="/waitlist"
        replace
      />
    );
  }

  // Approved but onboarding not complete — bounce to `/` (the auth/onboarding
  // entry point). The check against `/` prevents an infinite redirect loop
  // when ProtectedRoute happens to wrap a route mounted at `/`.
  if (onboardingCompletedAt === null && location.pathname !== '/') {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  // Onboarded but in no family: back to `/`, where <Onboarding /> shows the
  // create-or-join fork for exactly this case (the same hook decides both, so
  // the two gates cannot disagree and loop). While the server is asked, hold
  // the skeleton rather than flash an empty shell. A failed check is
  // 'unknown' and falls through to today's behaviour.
  if (householdless === 'checking' && !shownChildren.current) {
    return <AppSkeleton />;
  }
  if (householdless === 'none') {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  // Already completed onboarding — don't allow re-entering the auth gate.
  // `<Onboarding />` at `/` already handles this for unauth'd/completed
  // flows, but this branch catches ProtectedRoute-wrapped mounts of `/`.
  if (onboardingCompletedAt !== null && location.pathname === '/') {
    return (
      <Navigate
        to="/today"
        replace
      />
    );
  }

  shownChildren.current = true;
  return <>{children}</>;
}
